
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import AssessmentForm from "@/components/recruiter/assessments/ai-assessments/AssessmentForm.vue";
import EditAssessmentHeader from "@/components/recruiter/assessments/ai-assessments/AssessmentHeader.vue";
import QuestionModal from "@/components/recruiter/assessments/ai-assessments/QuestionModal.vue";
import QuestionsSection from "@/components/recruiter/assessments/ai-assessments/QuestionsSection.vue";
import PaginationControls from "@/components/shared/PaginationControls.vue";
import FloatingChatWidget from "@/components/shared/FloatingChatWidget.vue";

import {
  Assessment,
  AssessmentQuestion
} from "@/interfaces/recruiter/candidates/create_assessment";
import { SITE_DIRECTION } from "@/store/modules/common/constants";
import { SiteDirections } from "@/store/modules/common/interfaces";
import Vue from "vue";
import { mapGetters } from "vuex";

export default Vue.extend({
  name: "EditAssessment",
  components: {
    EditAssessmentHeader,
    AssessmentForm,
    QuestionsSection,
    QuestionModal,
    PaginationControls,
    FloatingChatWidget
  },
  data() {
    return {
      saving: false,
      deleting: false,
      isFormValid: true,
      showQuestionModal: false,
      showDeleteQuestionModal: false,
      isEditingQuestion: false,
      editingQuestion: null as AssessmentQuestion | null,
      questionToDelete: null as AssessmentQuestion | null,
      assessment: {
        id: 0,
        assessment_name: "",
        assessment_type: "custom" as const,
        difficulty: "easy" as const,
        is_active: true,
        total_questions: 0,
        created_by: 0,
        assigned_applicant_ids: [] as string[],
        assigned_applicants: [] as Array<{
          id: string;
          name: string;
          email: string;
          assigned_at: string;
        }>,
        total_assigned_applicants: 0
      } as Assessment,
      questions: [] as (AssessmentQuestion & { order?: number })[],
      page: 1,
      limit: 10,
      total: 0,
      limitOptions: [5, 10, 20, 50, 100],
      loading: false,

      questionTypes: [
        { text: "Multiple Choice", value: "mcq" },
        { text: "True/False", value: "true_false" },
        { text: "Short Answer", value: "short_answer" }
      ],
      rules: {
        required: (v: string) =>
          !!v || this.$t("candidate.assessments.create.required-field"),
        positive: (v: number) =>
          v > 0 || this.$t("candidate.assessments.create.positive-number"),
        scoreRange: (v: number) =>
          (v >= 0 && v <= 100) ||
          this.$t("candidate.assessments.create.score-range")
      }
    };
  },
  computed: {
    ...mapGetters("common", {
      get_site_direction: SITE_DIRECTION
    }),

    SiteDirections() {
      return SiteDirections;
    },
    questionForModal() {
      if (!this.editingQuestion) {
        return {
          id: 0,
          question_text: "",
          question_type: "mcq",
          question_options: [],
          correct_answer: "",
          question_time_allowed: 0,
          is_question_enable: true,
          order: this.questions?.length + 1
        };
      }

      return {
        ...this.editingQuestion,
        order: this.isEditingQuestion
          ? this.editingQuestion.order
          : this.questions?.length + 1
      };
    },
    aiSessionId() {
      return this.$store.getters["recruiter/getAiSessionId"];
    },
    aiSessionCreated() {
      return this.$store.getters["recruiter/getAiSessionCreated"];
    },
    aiSessionMessage() {
      return this.$store.getters["recruiter/getAiSessionMessage"];
    }
  },
  methods: {
    async fetchAssessment() {
      const assessmentId = this.$route.params.id;
      if (!assessmentId) {
        return;
      }

      try {
        const result = await this.$store.dispatch(
          "recruiter/FETCH_AI_ASSESSMENT_DETAILS",
          assessmentId
        );

        if (result) {
          this.assessment = {
            id: result.id,
            assessment_name: result.assessment_name,
            assessment_type: result.assessment_type || "custom",
            difficulty: result.difficulty || "easy",
            is_active: result.is_active !== false,
            created_by: result.created_by || 0,
            total_questions: result.total_questions || 0,
            assigned_applicant_ids: result.assigned_applicant_ids || [],
            assigned_applicants: result.assigned_applicants || [],
            total_assigned_applicants: result.total_assigned_applicants || 0
          };
          await this.fetchAssessmentQuestions();
        } else {
          this.$store.commit("ROOT_ERROR", {
            message: "Assessment not found",
            type: "error"
          });
          this.$router.push("/recruiter/ai-assessments");
        }
      } catch (error) {
        console.error("Error fetching assessment:", error);
        this.$store.commit("ROOT_ERROR", {
          message: "Error loading assessment",
          type: "error"
        });
        this.$router.push("/recruiter/ai-assessments");
      }
    },
    async updateAssessment() {
      this.saving = true;
      try {
        const success = await this.$store.dispatch(
          "recruiter/UPDATE_AI_ASSESSMENT",
          {
            id: this.assessment.id,
            assessment_name: this.assessment.assessment_name,
            assessment_type: this.assessment.assessment_type,
            difficulty: this.assessment.difficulty,
            is_active: this.assessment.is_active,
            total_questions: this.total || this.questions?.length || 0,
            applicant_ids: this.assessment.assigned_applicant_ids
          }
        );

        if (success) {
          this.$store.commit(
            "ROOT_NOTIFICATION",
            "Assessment updated successfully"
          );
          this.$router.push("/recruiter/ai-assessments");
        } else {
          this.$store.commit("ROOT_ERROR", {
            message: "Failed to update assessment",
            type: "error"
          });
        }
      } catch (error) {
        console.error("Error updating assessment:", error);
        this.$store.commit("ROOT_ERROR", {
          message: "Error updating assessment",
          type: "error"
        });
      } finally {
        this.saving = false;
      }
    },

    goBack() {
      this.$router.push("/recruiter/ai-assessments");
    },

    onValidationChange(isValid: boolean) {
      this.isFormValid = isValid;
    },

    addQuestion() {
      this.isEditingQuestion = false;
      this.editingQuestion = {
        id: 0,
        question_text: "",
        question_type: "mcq",
        question_options: [],
        correct_answer: "",
        question_time_allowed: 0,
        is_question_enable: true,
        order: this.questions?.length + 1
      };
      this.showQuestionModal = true;
    },

    editQuestion(question: AssessmentQuestion) {
      this.isEditingQuestion = true;
      this.editingQuestion = { ...question };
      this.showQuestionModal = true;
    },

    async saveQuestion(question: AssessmentQuestion) {
      try {
        if (this.isEditingQuestion) {
          // Update existing question via API
          const questionToUpdate = this.editingQuestion;
          if (questionToUpdate && questionToUpdate.id) {
            await this.$store.dispatch(
              "recruiter/UPDATE_AI_ASSESSMENT_QUESTION",
              {
                assessmentId: this.assessment.id,
                questionId: questionToUpdate.id,
                question: {
                  is_question_enable: question.is_question_enable,
                  question_text: question.question_text,
                  question_type: question.question_type,
                  question_options: question.question_options,
                  correct_answer: question.correct_answer,
                  question_time_allowed: question.question_time_allowed
                }
              }
            );

            // Update local state
            const index = this.questions?.findIndex(
              (q) => q.id === questionToUpdate.id
            );
            if (index !== -1) {
              const updatedQuestion = {
                ...this.questions?.[index],
                ...question,
                order: questionToUpdate.order
              };
              this.$set(this.questions, index, updatedQuestion);
            }
          }
        } else {
          if (question.order) {
            delete question.order;
          }

          // Add new question via API
          const response = await this.$store.dispatch(
            "recruiter/ADD_AI_ASSESSMENT_QUESTION",
            {
              assessmentId: this.assessment.id,
              questions: [
                {
                  is_question_enable: true,
                  question_text: question.question_text,
                  question_type: question.question_type,
                  question_options: question.question_options,
                  correct_answer: question.correct_answer,
                  question_time_allowed: question.question_time_allowed
                }
              ]
            }
          );

          if (response) {
            this.questions?.push(...[...response?.questions]);

            // Update total count
            this.total = (this.total || 0) + 1;
          }
        }

        this.showQuestionModal = false;
        this.editingQuestion = null;
      } catch (error) {
        console.error("Error saving question:", error);
        this.$store.commit("ROOT_ERROR", {
          message: "Error saving question",
          type: "error"
        });
      }
    },

    cancelQuestionEdit() {
      this.showQuestionModal = false;
      this.editingQuestion = null;
    },

    deleteQuestion(question: AssessmentQuestion) {
      this.questionToDelete = question;
      this.showDeleteQuestionModal = true;
    },

    async confirmDeleteQuestion() {
      if (!this.questionToDelete) return;

      try {
        this.deleting = true;

        // Delete question via API
        const response = await this.$store.dispatch(
          "recruiter/DELETE_AI_ASSESSMENT_QUESTION",
          {
            assessmentId: this.assessment.id,
            questionId: this.questionToDelete.id
          }
        );

        if (response) {
          // Remove from local state
          const index = this.questions?.findIndex(
            (q) => q.id === this.questionToDelete?.id
          );

          if (index !== -1) {
            this.questions?.splice(index, 1);

            // Reorder remaining questions
            this.questions?.forEach((q, i) => {
              if (q) {
                q.order = i + 1;
              }
            });

            // Update total count
            this.total = Math.max(0, (this.total || 0) - 1);
          }
        }
      } catch (error) {
        console.error("Error deleting question:", error);
        this.$store.commit("ROOT_ERROR", {
          message: "Error deleting question",
          type: "error"
        });
      } finally {
        this.deleting = false;
        this.showDeleteQuestionModal = false;
        this.questionToDelete = null;
      }
    },

    cancelDeleteQuestion() {
      this.showDeleteQuestionModal = false;
      this.questionToDelete = null;
    },

    reorderQuestions(questions: (AssessmentQuestion & { order?: number })[]) {
      this.questions = questions.map((q, index) => ({
        ...q,
        order: index + 1
      }));
    },
    async fetchAssessmentQuestions() {
      this.loading = true;
      const response = await this.$store.dispatch(
        "recruiter/FETCH_AI_ASSESSMENT_QUESTIONS",
        {
          assessmentId: this.assessment.id,
          query: { page: this.page, limit: this.limit }
        }
      );
      const { results = [], total = 0 } = response?.data || {};
      this.questions =
        results.map((q: AssessmentQuestion, index: number) => ({
          id: q.id,
          order: q.order || index + 1,
          question_text: q.question_text,
          question_type: q.question_type,
          question_options: q.question_options,
          correct_answer: q.correct_answer,
          question_time_allowed: q.question_time_allowed,
          is_question_enable: q.is_question_enable
        })) || [];
      this.total = total;
      this.loading = false;
    },
    async onPageChange(page: number) {
      this.page = page;
      await this.fetchAssessmentQuestions();
      // Send message to AI assistant
      await this.sendMessageToAIAssistant({
        ...this.assessment,
        questions: this.questions
      });
    },
    async onLimitChange(limit: number) {
      this.limit = limit;
      this.page = 1;
      await this.fetchAssessmentQuestions();
      await this.sendMessageToAIAssistant({
        ...this.assessment,
        questions: this.questions
      });
    },

    async onAssessmentUpdated(isNewQuestionAdded: boolean) {
      // Refresh the assessment questions after AI updates
      await this.fetchAssessment();
      await this.fetchAssessmentQuestions();
      if (isNewQuestionAdded) {
        await this.sendMessageToAIAssistant({
          ...this.assessment,
          questions: this.questions
        });
      }

      // Show success message
      this.$store.commit("ROOT_NOTIFICATION", {
        message: "Assessment updated with AI assistance",
        type: "success"
      });
    },
    async sendMessageToAIAssistant(message: {
      assessment: Assessment;
      questions: AssessmentQuestion[];
    }) {
      await this.$store.dispatch("recruiter/CHAT_WITH_AI_ASSISTANT", {
        session_id: this.aiSessionId,
        message: JSON.stringify(message)
      });
    },

    async createAISession() {
      if (this.aiSessionCreated) return;

      try {
        const sessionData = {
          id: this.assessment.id,
          assessment_type: this.assessment.assessment_type || "custom",
          assessment_name: this.assessment.assessment_name,
          difficulty: this.assessment.difficulty || "easy",
          is_active: this.assessment.is_active !== false,
          created_at: new Date().toISOString(),
          total_questions: this.assessment.total_questions?.toString() || "0",
          questions: this.questions || []
        };

        const response = await this.$store.dispatch(
          "recruiter/CREATE_AI_ASSESSMENT_SESSION",
          sessionData
        );

        if (response) {
          this.$store.commit("ROOT_NOTIFICATION", {
            message: "AI session created successfully",
            type: "success"
          });
        }
      } catch (error) {
        console.error("Error creating AI session:", error);
        this.$store.commit("ROOT_ERROR", {
          message: "Failed to create AI session",
          type: "error"
        });
      }
    }
  },
  async mounted() {
    await this.fetchAssessment();
    await this.createAISession();
  }
});
